import { holdsPermission, type StaffSession } from '@/lib/auth/session'
import type { ApiClient } from '@/lib/api'
import { ApiError } from '@/lib/api/errors'
import type { FormLine, FormPanel, RowColumn } from '@/kova/spec/types'

import { readCaller, type Caller } from './handles'

/**
 * The plumbing every write control shares.
 *
 * # Why this is one module and not a habit each adapter picks up
 *
 * `Decide` — the handle judgement from #111 — worked out the shape of a write
 * on this console: read the caller's own authority beside the screen, refuse in
 * the operator's vocabulary rather than the permission system's, and turn every
 * answer the backend can give into something the operator reads rather than an
 * exception that collapses the screen.
 *
 * That shape has to be identical on every screen or it is worse than useless.
 * An operator who learns that a dead button always says why will trust a dead
 * button that says nothing; a refusal worded three different ways across three
 * sections reads as three different problems. So the wording, the ordering of
 * the refusals and the failure-to-panel conversion live here, and an adapter
 * supplies only what is specific to its act: the permission, the role that
 * holds it, and what the act is called.
 *
 * # Nothing here decides whether an operator may write
 *
 * The backend does, and this only draws what it reported (Part I §5). Every
 * unknown resolves to "not held": a `/me` that refused, failed, or is not
 * deployed tells us nothing about this operator's authority, and the honest
 * rendering of not knowing is the disabled control with the reason said out
 * loud. The alternative — enabling on an unread permission — draws a control
 * that promises an act the server will refuse.
 */

/**
 * Why this operator may not perform this act, or `''` when they may.
 *
 * Three refusals, in the order they override each other, and the order is not
 * cosmetic: it is the order in which an operator can do something about them.
 *
 * The standby cell comes first because no permission changes it — a write to a
 * standby cell is refused by the backend whatever this console drew, so telling
 * an operator about a missing role there would send them to ask for something
 * that would not help (law 19).
 *
 * The permission refusal names **the role**, in the words an operator would use
 * to ask for it. `referral.scheme.write` is not something anybody can request;
 * "the Growth role" is. Law 18 requires the control be shown disabled with the
 * required role named, and naming a permission key instead satisfies the letter
 * of it while defeating the point.
 */
export function writeRefusal(
  caller: Caller,
  permission: string,
  role: string,
  act: string,
): string {
  if (caller.session?.cell?.role === 'standby') {
    return `Disabled — this is the standby cell. ${act} happens on the primary, and this backend refuses the write whatever is drawn here.`
  }
  if (caller.session === null) {
    return `Disabled — this console could not read what your session may do, so it is treating the permission as not held. ${caller.reason}`
  }
  if (!holdsPermission(caller.session, permission)) {
    return `Disabled — requires the ${role} role. ${act} needs the permission ${permission}. Everything above is readable from this session and none of it is actionable.`
  }
  return ''
}

/**
 * What the backend answered, as something the operator reads.
 *
 * # Every failure is a panel, and none of them is an exception
 *
 * A `FormSubmit` never rejects, for the same reason a `ScreenLoader` never
 * does: an exception out of here unmounts the control and takes the operator's
 * typing with it, at the exact moment they most need to know what happened.
 *
 * # `final` is the field that matters, and it is not "did it fail"
 *
 * It asks whether there is anything left to submit. A 4xx means the backend
 * considered the request and refused it — sending it again unchanged produces
 * the same refusal, so the control stays dead and the operator has to change
 * something or escalate. A 5xx or an unreachable backend means the request was
 * not decided; the operator may try again, and a control disabled after one of
 * those strands them.
 *
 * A 409 is deliberately in the second group. On this backend it is most often
 * a standby cell or a concurrent write, and both are worth retrying.
 */
export async function submitWrite(
  call: () => Promise<unknown>,
  heading: string,
  lines: (answer: unknown) => readonly FormLine[],
): Promise<FormPanel> {
  try {
    const answer = await call()
    return { tone: 'g', heading, lines: lines(answer), note: '', final: true }
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return failurePanel(error)
  }
}

/**
 * A refusal or a failure, told apart because an operator acts differently.
 *
 * The `decision_ref` is surfaced whatever the status — law 13 says every
 * refusal carries one and that screens must not swallow it. It is put on its
 * own line rather than into the sentence so it can be selected and quoted
 * without the prose around it.
 */
export function failurePanel(error: unknown): FormPanel {
  if (!(error instanceof ApiError)) {
    return {
      tone: 'r',
      heading: 'The backend could not be reached',
      lines: [],
      note: 'Nothing was written. The request never arrived, so it is safe to try again.',
      final: false,
    }
  }

  const lines: FormLine[] = [['status', String(error.status), '']]
  if (error.code !== null) lines.push(['code', error.code, ''])
  if (error.outcome !== null) {
    lines.push(['outcome', error.outcome, 'the authorisation seam’s answer (law 14)'])
  }
  if (error.decisionRef !== null) {
    lines.push(['decision_ref', error.decisionRef, 'quote this in the decision explorer'])
  }

  // The seam answered before the status did. See `seamPanel`.
  const seam = seamPanel(error, lines)
  if (seam !== null) return seam

  // 4xx: considered and refused. Resending it unchanged gets the same answer.
  const decided = error.status >= 400 && error.status < 500 && error.status !== 409
  return {
    tone: 'r',
    heading: decided ? 'The backend refused this' : 'The write did not complete',
    lines,
    note: decided
      ? `${error.message} Nothing was written.`
      : `${error.message} Whether anything was written is not known from here — check the record before trying again.`,
    final: decided,
  }
}

/**
 * The authorisation seam's answer, when it gave one, as its own panel.
 *
 * # Why the status is not enough
 *
 * `authz.Authorize` sits in front of every money-adjacent instruction and
 * answers with one of law 14's four outcomes. Three of them can come back as an
 * error, and the status alone cannot tell them apart from an ordinary refusal:
 *
 *   - **`step_up`** arrives as a **401**, and the contract is explicit about
 *     why: *"401 rather than 403 for a step-up because it is not a refusal of
 *     authority"*. Read as a bare 401 it is a 4xx, so the generic branch below
 *     calls it *refused* and marks it `final` — which kills the control on the
 *     one answer the operator could have acted on. They complete the step-up,
 *     come back, and find a dead button and a sentence saying they were
 *     refused. That is the exact stranding `final` was introduced to prevent,
 *     arriving through the one status nobody had a case for.
 *   - **`review`** arrives as a **403** and does not mean the operator lacks
 *     authority either. The instruction is queued for a second pair of eyes;
 *     saying "the backend refused this" sends them to ask for a role that
 *     would not help.
 *   - **`refuse_to_submit`** is the real refusal, and it is the only one of the
 *     three that should read as one.
 *
 * `allow` never arrives here — an allowed instruction is a 2xx.
 *
 * # This is presentation, and the outcome is the backend's
 *
 * Nothing here decides anything. The outcome is parsed off the wire by the
 * typed client, which is also the only place that could be wrong about it, and
 * an outcome this console does not know resolves to `null` and falls through to
 * the generic branch rather than being guessed at.
 */
function seamPanel(error: ApiError, lines: readonly FormLine[]): FormPanel | null {
  if (error.outcome === 'step_up') {
    return {
      tone: 'a',
      heading: 'A step-up is required before this can proceed',
      lines,
      note: `${error.message} Nothing was written, and this is NOT a refusal — the authorisation seam is asking you to do something first, which is why it answers 401 rather than 403. Complete the step-up and submit again; the control is deliberately still live.`,
      final: false,
    }
  }
  if (error.outcome === 'review') {
    return {
      tone: 'a',
      heading: 'The seam sent this for review',
      lines,
      note: `${error.message} Nothing was written and nothing was refused: the instruction needs a second pair of eyes before it can proceed. Quote the decision reference — asking for a role will not move it, because this is not about the authority you hold.`,
      final: true,
    }
  }
  if (error.outcome === 'refuse_to_submit') {
    return {
      tone: 'r',
      heading: 'The seam refused to submit this',
      lines,
      note: `${error.message} Nothing was written. This is a decision about the instruction rather than about your role, so it is answerable from the decision explorer and not by being granted anything.`,
      final: true,
    }
  }
  return null
}

/**
 * The caller's authority, read beside a screen and never left dangling.
 *
 * # Why this exists rather than calling `readCaller` directly
 *
 * A loader that reads the screen and the session concurrently starts two
 * promises and awaits the screen first. When the screen read throws — an
 * aborted navigation, a 503 — the session promise is never awaited, and if it
 * rejects there is nothing attached to it. That is an unhandled rejection: a
 * crash report in the browser, and a failing test run with no failing test.
 *
 * It is not hypothetical. `readCaller` resolves every error EXCEPT `AbortError`,
 * which it deliberately re-throws so a cancelled navigation is not mistaken for
 * a degraded read — and an aborted navigation aborts both requests, so this is
 * the common case rather than the rare one.
 *
 * # An aborted session read is "not held", and that is the honest answer
 *
 * The screen read's own abort still propagates and still cancels the screen;
 * only this promise's rejection is absorbed. If the session read was cancelled
 * we did not learn what the operator may do, which is exactly the state
 * `writeRefusal` renders as a disabled control with the reason said out loud.
 */
export function callerBeside(api: ApiClient): Promise<Caller> {
  return readCaller(api).catch((): Caller => ({
    session: null,
    reason: 'The session read did not complete.',
  }))
}

/**
 * The rows of a `pairs` field, as the array a body wants.
 *
 * The control carries its rows as JSON in the same string map every other field
 * uses, so nothing in the component needs a special case for it — this is where
 * that encoding is undone, once, rather than in each adapter.
 *
 * Rows with an empty half are dropped. A key with no body would publish an
 * empty string over whatever that key currently says, which is the one outcome
 * an editor would never intend and the hardest to notice afterwards.
 */
export function pairsFrom(
  raw: string | undefined,
  keyName: string,
  valueName: string,
): readonly Record<string, string>[] {
  if (raw === undefined || raw.trim() === '') return []
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return []
  }
  if (!Array.isArray(parsed)) return []
  const out: Record<string, string>[] = []
  for (const row of parsed) {
    const r = row as Record<string, unknown>
    const k = typeof r['k'] === 'string' ? r['k'].trim() : ''
    const v = typeof r['v'] === 'string' ? r['v'] : ''
    if (k === '' || v.trim() === '') continue
    out.push({ [keyName]: k, [valueName]: v })
  }
  return out
}

// ---------------------------------------------------------------------------
// Maker-checker
// ---------------------------------------------------------------------------

/**
 * The actor string the backend records, built from the session we hold.
 *
 * # This prefix is the whole function
 *
 * `staff.Principal.Actor()` is `"operator:" + Subject`, and every actor column
 * on every append-only table carries that form: `opened_by`,
 * `closure_proposed_by`, and the `actor` on each event of a case's history.
 * The console's session carries the BARE subject.
 *
 * Compare the two directly and nothing ever matches. That failure is silent and
 * it fails OPEN: the checker control renders live for the very operator who
 * proposed the closure, law 15 is defeated on the only screen that implements
 * it, and the operator discovers the rule from a 409 after composing the whole
 * thing. A refusal stated before the click and a refusal stated after are not
 * the same control.
 *
 * So the prefix lives here, once, with a test that pins it against the Go
 * encoding — and any other screen comparing a session to a recorded actor gets
 * it from here rather than deriving it again.
 *
 * `''` when there is no subject to build from, which is **not** the backend's
 * `"operator:unknown"` fallback and must not be spelled as it. That string is a
 * real value a real row can hold; returning it here would make an unidentified
 * session collide with every case some other unidentified session touched, and
 * the collision would read as a correct maker-checker refusal while being an
 * accident. Not knowing who the operator is has its own answer below.
 */
export function recordedActor(session: StaffSession | null): string {
  const subject = session?.subject ?? ''
  return subject === '' ? '' : `operator:${subject}`
}

/** Who a case records, in the two roles that bar a closure approval. */
export interface CaseActors {
  /** `opened_by` — the operator who raised the case. */
  readonly openedBy: string
  /** `closure_proposed_by` — the maker. Empty unless a closure is proposed. */
  readonly proposedBy: string
}

/**
 * Why this operator may not approve this closure, or `''` when they may.
 *
 * # Two rules, and the second is the one people miss
 *
 * `amlcase.ApproveClosure` refuses the proposer (`ErrSameOperator`) and, on top
 * of that, whoever raised the case (`ErrOpenerCannotApprove`). The second is
 * not redundant: an opener who proposes through a colleague and then approves
 * it themselves has resolved their own case with one extra step, and the
 * acceptance criterion is that no single operator can both raise and resolve
 * one. Each gets its own sentence because they send the operator to different
 * people — the first to anyone else, the second to anyone who is not them.
 *
 * # Not knowing is a refusal
 *
 * Two unknowns land here and both disable the control:
 *
 * An unidentified session cannot be told apart from the maker, and a control
 * that cannot enforce the rule must not offer the act. Enabling on an unread
 * identity would be the fail-open this function exists to prevent.
 *
 * An unattributed proposal — `closure_proposed_by` empty on a case that is in
 * `closure_proposed` — is the same hole from the other side. The queue's own
 * approval card already reads that state as `r`/unattributed for exactly this
 * reason: "a maker-checker control that cannot name the maker cannot enforce
 * that the maker is not the checker."
 *
 * # What this is not
 *
 * Not authorization. `ApproveClosure` runs these same two comparisons
 * server-side against its own record, on every call, whether or not the console
 * drew the button (Part I §5). This decides what to DRAW, and drawing it
 * honestly is the entire point: the refusal is a fact about who the operator is
 * on this specific case rather than about the role they hold, which is why the
 * backend answers it with 409 and not 403.
 */
export function closureApprovalRefusal(caller: Caller, actors: CaseActors): string {
  const me = recordedActor(caller.session)
  if (me === '') {
    return 'Disabled — this console could not establish who you are, so it cannot tell whether you are the operator who proposed this closure. The second signature is only a control if the two operators can be told apart.'
  }
  if (actors.proposedBy === '') {
    return 'Disabled — this case is awaiting a countersignature and the read did not name who proposed the closure. Until it does, nobody can be told whether they are the maker, so nobody may be offered the approval.'
  }
  if (me === actors.proposedBy) {
    return 'Disabled — you proposed this closure, and the operator who proposed a closure may not approve it. A second compliance officer has to countersign. Withdrawing the proposal is available to you; approving it is not.'
  }
  if (me === actors.openedBy) {
    return 'Disabled — you raised this case, and the operator who raised a case may not approve its closure. Proposing through a colleague and then approving it yourself would be resolving your own case with one extra step, which is the thing this rule exists to stop.'
  }
  return ''
}

/**
 * The rows of a `rows` field, decoded to what a body wants.
 *
 * # The three conversions, and why none of them may be an adapter's habit
 *
 * **Blank means absent, not zero.** A column declared `nullWhenBlank` emits
 * `null` for an empty cell. This is the whole reason `min_fee_minor` and
 * `max_fee_minor` are pointers on the wire: a console that sent `0` for an
 * untouched cap would set a cap of nothing chargeable on every line an operator
 * left alone, and the request would look exactly like a deliberate one.
 *
 * **Minor units stay integers.** `amount` cells are parsed with `BigInt` and
 * emitted as numbers only when they fit; a value that is not digits is dropped
 * with its row rather than coerced. `Number()` on a minor-unit string is how a
 * float reaches a money field (law 9), and `parseInt` would silently truncate
 * `"12abc"` to `12`.
 *
 * **Incomplete rows are dropped.** A row whose required cells are not all
 * filled is a line somebody started; sending it would publish a priced line
 * with no price. `answered` already refuses a form whose ONLY row is
 * incomplete, so dropping here cannot turn a submission into a silent no-op.
 *
 * Every one of those is a decision that has to be the same on every screen, so
 * it lives here rather than in each adapter — the same argument `pairsFrom`
 * records, with more at stake.
 */
export function rowsFrom(
  raw: string | undefined,
  columns: readonly RowColumn[],
): readonly Record<string, string | number | null>[] {
  if (raw === undefined || raw.trim() === '') return []
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return []
  }
  if (!Array.isArray(parsed)) return []

  const out: Record<string, string | number | null>[] = []
  for (const entry of parsed) {
    if (typeof entry !== 'object' || entry === null) continue
    const row = entry as Record<string, unknown>
    const cell = (name: string): string => {
      const v = row[name]
      return typeof v === 'string' ? v.trim() : ''
    }

    const touched = columns.some((c) => cell(c.name) !== '')
    if (!touched) continue
    if (columns.some((c) => c.required && cell(c.name) === '')) continue

    const built: Record<string, string | number | null> = {}
    let usable = true
    for (const c of columns) {
      const v = cell(c.name)
      if (c.kind !== 'amount') {
        built[c.name] = v
        continue
      }
      if (v === '') {
        // Absent, and said so explicitly. A column that is neither required
        // nor nullable and left blank is a zero the operator typed nothing
        // for, so it is refused with its row rather than guessed at.
        if (c.nullWhenBlank === true) built[c.name] = null
        else usable = false
        continue
      }
      if (!/^-?\d+$/.test(v)) {
        usable = false
        continue
      }
      const n = BigInt(v)
      if (n > BigInt(Number.MAX_SAFE_INTEGER) || n < BigInt(-Number.MAX_SAFE_INTEGER)) {
        // Beyond what JSON.stringify can carry without losing digits. Dropping
        // the row is wrong-but-loud; sending a rounded amount is wrong-and-quiet.
        usable = false
        continue
      }
      built[c.name] = Number(n)
    }
    if (usable) out.push(built)
  }
  return out
}

/**
 * Why this operator may not take the second half of a two-person act.
 *
 * # The general form of what `closureApprovalRefusal` does for one case
 *
 * Law 15 turns up in more than one shape. An AML closure bars two people for
 * two different reasons and needs both sentences; a break-glass elevation bars
 * one — whoever asked for it — from acknowledging it and from reviewing it
 * afterwards. That is the same comparison against the same actor encoding, so
 * it is the same function, and the CALLER supplies the sentence because the
 * sentence is the part that differs.
 *
 * Deriving the `operator:` prefix again at each of those sites is how one of
 * them ends up comparing bare subjects and failing open. It is computed here,
 * once, by `recordedActor`, which is pinned by test.
 *
 * # Not knowing refuses, in both directions
 *
 * An unidentified session cannot be told apart from the first actor. An
 * unrecorded first actor cannot be told apart from anybody. Either way the
 * control cannot enforce the rule, so it does not offer the act — the same
 * fail-closed posture every other refusal on this console takes.
 *
 * # The comparison is a PARAMETER, because this backend makes two of them
 *
 * `same` is required and has no default, and that is the whole reason it is a
 * parameter rather than a `===` in the body:
 *
 *   - `amlcase.ApproveClosure` and `storage/breakglass.go` compare with Go's
 *     `==`. Two actors differing by one letter's case are two people.
 *   - `referral.SameActor` compares with `strings.EqualFold` over the trimmed
 *     string, and says why: *"`Ops-1` and `ops-1` are one person and a rule
 *     that can be walked around with the shift key is not a rule."* Migration
 *     106's CHECK constraints compare the same way.
 *
 * A shared helper that picked one would be right on some screens and wrong on
 * the others, and the wrong direction is not symmetric: comparing a payout's
 * actors exactly enables the approve control for the operator who requested it
 * whenever the two rows differ in case, which is failing OPEN on the one rule
 * that exists to stop a single person moving the programme's money. A default
 * would put that failure one forgotten argument away, so there is none.
 */
export function secondPersonRefusal(
  caller: Caller,
  firstActor: string,
  /** What the first person did, in words: "requested this elevation". */
  didWhat: string,
  /** What the second person would be doing: "Acknowledging". */
  act: string,
  /** How this domain decides two actor strings name one person. Required. */
  same: ActorComparison,
): string {
  const me = recordedActor(caller.session)
  if (me === '') {
    return `Disabled — this console could not establish who you are, so it cannot tell whether you are the operator who ${didWhat}. ${act} is only a second pair of eyes if the two people can be told apart.`
  }
  if (firstActor === '') {
    return `Disabled — this read did not name who ${didWhat}, so nobody can be told whether they are that person. ${act} is not offered to anybody until it does.`
  }
  if (same(me, firstActor)) {
    return `Disabled — you ${didWhat}. ${act} is somebody else's, and that is the whole of what makes it a control rather than a formality.`
  }
  return ''
}

/**
 * How a domain decides whether two recorded actors are one person.
 *
 * Two implementations because this backend has two rules — see
 * `secondPersonRefusal`. Neither is a default and neither is "the right one":
 * each mirrors the comparison the code that will actually refuse the write
 * makes, and a screen using the other one draws a control the server disagrees
 * with in one direction or the other.
 */
export type ActorComparison = (mine: string, theirs: string) => boolean

/**
 * Go's `==`. What `amlcase.ApproveClosure` and `storage/breakglass.go` do.
 */
export const actorsAreIdentical: ActorComparison = (mine, theirs) => mine === theirs

/**
 * `strings.EqualFold(strings.TrimSpace(a), strings.TrimSpace(b))`. What
 * `referral.SameActor` does, and what migration 106's CHECK constraints do.
 *
 * `toLowerCase()` and not `toLocaleLowerCase()`: Go's fold is locale-independent
 * simple case-folding, and a locale-aware lower-casing disagrees with it — a
 * Turkish locale maps `I` to a dotless `ı`, so two actors the database calls one
 * person would read here as two, and the control would go live for the maker.
 * The browser's locale is not a fact about who requested a payout.
 *
 * Where the two implementations still disagree on some exotic pair, the
 * DATABASE is the enforcement point and refuses the row; this decides only what
 * to draw.
 */
export const actorsAreTheSamePerson: ActorComparison = (mine, theirs) =>
  mine.trim().toLowerCase() === theirs.trim().toLowerCase()
